import React, {
  FunctionComponent,
  useMemo,
  useContext,
  useCallback,
  useState,
} from 'react';
import EDView from '../../../../../../BaseComponents/View/EDView';
import { InternalError } from '../../../../../../Components/InternalError/InternalError';
import {
  IClientBusinessModel,
  IClientAssociationModel,
} from '../../../../../../Types/ClientModelTypes';
import { IColumnDef } from '../../../../../../Types/ColumnDefType';
import {
  findCollectionStatic,
  findCollectionStaticSafe,
} from '../../../../../../Model/avenyStaticModelUtils';
import MasterComp from './AssociationObjectListMaster';
import { MdModalRoute } from '../../../../../../Components/MasterDetail/Components/MdModal/MdModalRoute';
import { TVisningstyp } from '../BusinessDataSet/BusinessDataSet';
import MdInline from '../../../../../../Components/MasterDetail/Components/MdInline/MdInline';
import { IMenuProps } from '../../FunctionMenu/FunctionMenu/FunctionMenu';
import { VisningsTypContext } from '../VisningsTypContext/VisningsTypContext';
import { useItemFunctionMenuItemsType } from '../../../../../../Utils/ObjektVyUtils';
import NodeQuery, {
  INodeData,
} from '../../../../../../Components/QueryComponents/NodeQuery/NodeQuery';
import { debugObjectDetail } from '../../../../../Dev/DebugRutin/DebugObjectDetail/DebugObjectDetail';

export type UpdateAssociation = (
  id: string,
  association: IClientAssociationModel,
  setDetail: (id: string) => void
) => void;

export interface IAssociationListConfig {
  customDefaultColumns?: IColumnDef[];
  inCard?: boolean;
  detailsFullPage?: boolean;
  defaultVisningstyp?: TVisningstyp;
  noCaption?: boolean;
  useFunktionsMenyItems?: useItemFunctionMenuItemsType;
  customSOLabel?: string;
  disabled?: boolean;
}

export interface IAssociationObjectListProps {
  /** id för objektet som associationen utgår från */
  ownId: string | null;
  /** collectionName för objektet som associationen utgår från */
  collectionName: string;
  associationObjects: IClientAssociationModel[];
  /** Objekt för inställningar som inte är obligatoriska.
   *  customDefaultColumns, defaulta till andra kolumner än de som är default för typen.
   *  ska kompletteras med fler inställning för att välja andra list och detaljvyer
   */
  config?: IAssociationListConfig;
  associatedCollectionFieldName?: string;
  vyMenu?: (props: IMenuProps) => React.ReactElement<any>;
}

interface IInnerObjectListProps extends IAssociationObjectListProps {
  data: INodeData;
}

export interface IAssociationObjectListInfo {
  association: IClientAssociationModel;
  collection: IClientBusinessModel;
  id: string;
}

/**
Listar de associerade objekten för en "till många"-association.
*/
const AssociationList: FunctionComponent<
  React.PropsWithChildren<IInnerObjectListProps>
> = props => {
  const [association, setAssociation] =
    useState<IAssociationObjectListInfo | null>(null);

  const updateAssociation: UpdateAssociation = useCallback(
    (
      id: string,
      association: IClientAssociationModel,
      setDetail: (id: string) => void
    ) => {
      const collection = findCollectionStaticSafe(
        association.AssociatedCollectionName,
        'felaktig association'
      );
      setAssociation({
        association: association,
        collection: collection,
        id: id,
      });
      setDetail(`${association.AssociationName}/${id}`);
    },
    []
  );
  const unselectAssociation = useCallback(() => {
    setAssociation(null);
  }, []);
  const { visningsTyp } = useContext(VisningsTypContext);
  const collection = useMemo(
    () => findCollectionStatic(props.collectionName),
    // eslint-disable-next-line react-hooks/exhaustive-deps
    []
  );

  const masterConfig = useMemo(
    () =>
      collection && {
        comp: MasterComp(
          props.associationObjects,
          updateAssociation,
          unselectAssociation,
          props.data,
          association
        ),
        caption: collection.RootType.SBLabel,
      },
    [
      collection,
      props.associationObjects,
      props.data,
      updateAssociation,
      unselectAssociation,
      association,
    ]
  );
  const detailConfig = useMemo(
    () => debugObjectDetail(association),
    [association]
  );
  if (collection && masterConfig && detailConfig) {
    return (
      <EDView
        style={
          props.config?.inCard
            ? { width: '100%', display: 'flex' }
            : { width: '100%', height: '100%', display: 'flex' }
        }
      >
        {visningsTyp !== 'expanderbar lista' && (
          <MdModalRoute
            detailRouteName={association?.association.AssociationName}
            master={masterConfig}
            detail={detailConfig}
            collection={collection}
          />
        )}
        {visningsTyp === 'expanderbar lista' && (
          <MdInline master={masterConfig} detail={detailConfig} />
        )}
      </EDView>
    );
  }
  return (
    <InternalError
      message={`CollectionName ${props.collectionName} associationObjectList saknas`}
      id={'b4362ff1-6ddd-46f1-9d94-7866000a71c3'}
    />
  );
};

export interface ICreateAssociationListConfig {
  ownId?: string;
  collectionName: string;
  associationName: string;
  associatedCollectionFieldName: string;
  config: { customDefaultColumns?: IColumnDef[] };
}

const EnhancedAssociationList: React.FC<
  React.PropsWithChildren<IAssociationObjectListProps>
> = props => {
  return (
    <NodeQuery
      collectionName={props.collectionName}
      id={props.ownId}
      fieldDefs={props.associationObjects.map(x => `${x.AssociationName}.id`)}
      caption={props.collectionName}
    >
      {(loading, data) => {
        if (loading) {
          return null;
        }
        if (!data) {
          return null;
        }
        return <AssociationList data={data} {...props} />;
      }}
    </NodeQuery>
  );
};

export default EnhancedAssociationList;
